import React, { useState } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { AiOutlinePlus } from 'react-icons/ai';
import { DevTool } from '@hookform/devtools';
import Templates from './Templates';
import useAddTemplateForm from './useAddTemplateForm';
import TemplateForm from './TemplateForm';
import * as S from '../style';
import { Template } from '../../../@types/domain';
import { Popup } from '../../../components';
import TextField from '../../../components/TextField';
import useGetTemplates from './useGetTemplates';

function TemplatesTab() {
  const [activeTemplate, setActiveTemplate] = useState<Template | undefined>();
  const [isAddTemplateFormPopupOpen, setIsAddTemplateFormPopupOpen] =
    useState<boolean>(false);

  const {
    data: templates,
    isFetching: isFetchingTemplates,
    isLoading: isLoadingTemplates,
    isSuccess: isSuccessTemplates,
    error: errorTemplates,
  } = useGetTemplates({
    onSuccess(data) {
      if (data.length > 0 && !activeTemplate) {
        setActiveTemplate(data[0]);
      }
      if (data.length === 0) {
        setActiveTemplate(undefined);
      }
    },
  });

  const isEmptyTemplates = templates?.length === 0;

  const { register, onSubmit, error, isLoading, formState, control, reset } =
    useAddTemplateForm(templates || [], {
      onSuccess: (data) => {
        setActiveTemplate(data);
        setIsAddTemplateFormPopupOpen(false);
      },
    });
  const { isValid, errors, isValidating } = formState;

  function handleToggleAddTemplatePopup(
    event: React.MouseEvent<HTMLButtonElement>
  ) {
    event?.preventDefault();
    setIsAddTemplateFormPopupOpen((previousState) => !previousState);
    reset();
  }

  return (
    <Row>
      <Col md={4} lg={3}>
        <S.TemplateListContainer>
          <S.AddTemplateButton
            type="button"
            onClick={handleToggleAddTemplatePopup}
            disabled={isLoadingTemplates}
          >
            <AiOutlinePlus />
            Add Template
          </S.AddTemplateButton>

          {isEmptyTemplates && (
            <S.EmptyTemplates>
              No templates have been created yet
            </S.EmptyTemplates>
          )}
          {!isLoadingTemplates && isSuccessTemplates && (
            <Templates
              templates={templates}
              activeTemplateId={activeTemplate?.id}
              onTemplateSelected={setActiveTemplate}
            />
          )}
          {errorTemplates && (
            <S.ErrorText>{errorTemplates.message}</S.ErrorText>
          )}
          {isLoadingTemplates || (isFetchingTemplates && <p>Loading...</p>)}

          {isAddTemplateFormPopupOpen && (
            <form onSubmit={onSubmit}>
              <Popup
                open
                title="Add template"
                body={
                  <>
                    <S.AddInput>
                      <TextField
                        placeholder="Add new template"
                        disabled={isLoading}
                        error={!!errors.name}
                        autoFocus={true}
                        {...register('name')}
                      />
                    </S.AddInput>
                    {!isLoading && error && (
                      <S.ErrorText>{error.message}</S.ErrorText>
                    )}
                    {errors.name && (
                      <S.ErrorText>{errors.name.message}</S.ErrorText>
                    )}
                    <DevTool control={control} placement="top-left" />
                  </>
                }
                cancelLabel="Cancel"
                onCancel={handleToggleAddTemplatePopup}
                confirmLabel="Add"
                disabledConfirm={!isValid || isLoading || isValidating}
              />
            </form>
          )}
        </S.TemplateListContainer>
      </Col>
      <Col md={8} lg={9}>
        {activeTemplate && (
          <S.FormContainer>
            <TemplateForm key={activeTemplate.id} template={activeTemplate} />
          </S.FormContainer>
        )}
      </Col>
    </Row>
  );
}

export default TemplatesTab;
